import { GithubLogo, UserCircle } from '@phosphor-icons/react';
import { NmblrMark } from '@/components/nmblr-logo';
import { NavLink } from './nav-link';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useAuth, identityLabel } from '@/context/auth';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const NAV: NavSection[] = [
  {
    label: 'Browse',
    items: [
      { to: '/', label: 'Overview', icon: 'overview', end: true },
      { to: '/repositories', label: 'Repositories', icon: 'repositories' },
      { to: '/sync-issues', label: 'Sync issues', icon: 'sync-issues' },
      { to: '/search', label: 'Search', icon: 'search' },
      { to: '/graph', label: 'Dependency graph', icon: 'graph' },
    ],
  },
];

/**
 * Signed-in identity block (RFC-0009 Task 2.3). Reads the current user from
 * `AuthProvider` (which bootstraps `GET /api/me`) and shows the resolved name
 * when Kratos supplied one, falling back to the email otherwise. Renders a quiet
 * placeholder while the bootstrap is in flight and nothing once no identity is
 * available (defensive — the shell only mounts for authenticated sessions).
 */
function SidebarIdentity() {
  const { identity, isLoading } = useAuth();
  const label = identityLabel(identity);

  if (isLoading && !label) {
    return (
      <div className="flex items-center gap-2 px-3 pb-3" aria-hidden="true">
        <UserCircle size={20} className="shrink-0 text-text/25" />
        <span className="h-3 w-24 animate-pulse rounded bg-overlay/10" />
      </div>
    );
  }

  if (!label) return null;

  return (
    <div className="flex items-center gap-2 px-3 pb-3">
      <UserCircle size={20} weight="fill" className="shrink-0 text-text/40" />
      <span className="truncate text-[13px] font-medium text-text/70" title={label}>
        {label}
      </span>
    </div>
  );
}

interface SidebarProps {
  /**
   * When rendered as the body of the mobile app-shell drawer (RFC-0014 Task 1.4),
   * fill the drawer panel and drop the floating-island chrome (outer margin,
   * right-rounded corner, float shadow) plus the `hidden md:flex` inline gating.
   * Default `false` — the inline desktop sidebar, visible at `md`+ and hidden
   * below `md` (restores the mockup's `hidden md:flex`, graph-cytoscape.html:41).
   */
  inDrawer?: boolean;
}

/**
 * Persistent floating sidebar — solid `--c-sidebar` panel with a large
 * right-rounded corner and a diffused float shadow, mirroring the
 * "flush" shell from nmblr.foundation.app. The gradient backdrop shows
 * behind transparent page content; this panel sits on top as a solid island.
 *
 * Two variants (single implementation — RFC-0014 §1 "CSS/behavioral fork, not
 * two implementations"):
 *   - inline (default): `hidden md:flex` — the desktop sidebar.
 *   - `inDrawer`: fills the off-canvas Drawer panel below `md`.
 */
export function Sidebar({ inDrawer = false }: SidebarProps = {}) {
  return (
    <aside
      className={
        inDrawer
          ? 'flex h-full w-full flex-col overflow-hidden'
          : 'hidden md:flex flex-col w-60 shrink-0 my-7 overflow-hidden rounded-r-[50px]'
      }
      style={
        inDrawer
          ? { background: 'var(--c-sidebar)' }
          : {
              background: 'var(--c-sidebar)',
              // Diffused, layered float shadow — soft near layer + wide blurred
              // far layer (negative spread keeps the edges soft, not a hard band).
              boxShadow:
                '0 12px 32px -10px rgba(0,0,0,0.14), 0 40px 80px -24px rgba(0,0,0,0.20)',
            }
      }
    >
      {/* Logo */}
      <div
        className="px-5 pt-6 pb-5"
        style={{ borderBottom: '1px solid var(--c-border-on-accent)' }}
      >
        <div className="flex items-center gap-2.5">
          <div className="h-7 px-2 rounded-lg flex items-center bg-overlay/10">
            <NmblrMark size={16} className="block" gradient />
          </div>
          <span className="text-xs text-text/40">docs</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-6 flex-1 px-3 py-5 overflow-y-auto">
        {NAV.map((section) => (
          <div key={section.label}>
            <p className="text-[10px] font-semibold uppercase tracking-widest px-3 mb-1.5 text-text/30">
              {section.label}
            </p>
            <div className="flex flex-col gap-0.5">
              {section.items.map((item) => (
                <NavLink key={item.to} {...item} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Configuration + theme + source */}
      <div
        className="px-3 py-4"
        style={{ borderTop: '1px solid var(--c-border-on-accent)' }}
      >
        <SidebarIdentity />
        <NavLink to="/settings" label="Configuration" icon="settings" />
        <div className="flex items-center justify-between px-3 mt-3">
          <span className="text-[10px] text-text/30">v0.1.0</span>
          <div className="flex items-center gap-3">
            <a
              href="https://github.com/talino-labs/nmblr.document-portal"
              target="_blank"
              rel="noopener noreferrer"
              className="text-text/40 hover:text-text/80 transition-colors"
              title="View source on GitHub"
            >
              <GithubLogo size={15} />
            </a>
            <ThemeToggle />
          </div>
        </div>
      </div>
    </aside>
  );
}
